'use client';
import * as React from 'react';
import { Description, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn, getTestId } from '../../utils';

export interface BottomSheetProps {
  /** Whether the sheet is shown */
  open: boolean;
  /** Called on Esc and a click on the backdrop */
  onClose: () => void;
  /** Sheet content; name it with `BottomSheet.Title` unless `aria-label` is given */
  children: React.ReactNode;
  /** Accessible name of the dialog */
  'aria-label'?: string;
  /** Additional classes for the sheet surface (background, overflow) */
  className?: string;
  testId?: string;
  id?: string;
}

/**
 * Internal modal sheet that slides up from the bottom over a backdrop.
 * Shared by `FilterSheet` and the narrow-screen mode of `SidePanel`.
 */
const BottomSheetBase = React.forwardRef<HTMLDivElement, BottomSheetProps>(
  ({ open, onClose, children, 'aria-label': ariaLabel, className, testId, id }, ref) => (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          as='div'
          className='relative z-[100]'
          aria-label={ariaLabel}
        >
          <motion.div
            key='backdrop'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className='fixed inset-0 bg-overlays-50'
          />
          <div className='fixed inset-0 flex flex-col justify-end'>
            <motion.div
              key='sheet'
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className='w-full'
            >
              <DialogPanel
                ref={ref}
                id={id}
                data-testid={getTestId({ testId, id })}
                className={cn('flex max-h-[90dvh] flex-col rounded-t-2xl shadow-xl', className)}
              >
                {children}
              </DialogPanel>
            </motion.div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  )
);

BottomSheetBase.displayName = 'BottomSheet';

export const BottomSheet = Object.assign(BottomSheetBase, {
  /** Heading that names the sheet */
  Title: DialogTitle,
  /** Text that describes the sheet */
  Description: Description
});
